import { XStack, YStack } from '@mythical/ui'
import { HomepageFeatured } from 'app/blocks/homepage/homepage-featured'
import ContentCardFeatured, {
  ContentCardFeaturedProps,
} from 'app/components/content/ContentCardFeatured'
import {
  ContentCarousel,
  type SizeConfig,
} from 'app/components/content/ContentCarousel'
import { Button } from 'app/components/primitive/Button'
import {
  IconChevronLeft,
  IconChevronRight,
} from 'app/components/primitive/Icon'
import { PaginationDots } from 'app/components/primitive/PaginationDots'
import Section from 'app/components/structure/Section'
import { useCarouselController } from 'app/hooks/use-carousel-controller'
import { useCallback } from 'react'

const sizeConfig: SizeConfig = {
  width: '100%',
  spacing: 8,
  $gtXs: {
    width: '100%',
    spacing: 8,
  },
  $gtSm: {
    width: '50%',
    spacing: 16,
  },
}

export const HomepageFeaturedBlock: React.FC<{ block: HomepageFeatured }> = ({
  block,
}) => {
  const { title, content } = block || {}
  const { carouselRef, ...controller } = useCarouselController()

  if (!content || content?.length === 0) {
    return null
  }

  const renderItem = useCallback(
    (item) => (
      <ContentCardFeatured
        {...item}
        image={item.image2x}
        {...(item.backgroundColour &&
          item.foregroundColour && {
            backgroundColour: item.backgroundColour,
            foregroundColour: item.foregroundColour,
          })}
      />
    ),
    [],
  )

  const keyExtractor = useCallback((item) => item.id, [])

  return (
    <Section
      paddedX
      title={title}
      titleProps={{ $sm: { dsp: 'none' } }}
      $sm={{ gap: '$0' }}
      customCta={
        <XStack gap="$2" $sm={{ dsp: 'none' }}>
          <Button
            theme="white"
            onPress={controller.scrollToPrevious}
            disabled={!controller.canScrollPrevious}
            aria-label="Previous"
            icon={<IconChevronLeft size={16} />}
            size="$3.5"
            circular
          />
          <Button
            theme="white"
            onPress={controller.scrollToNext}
            disabled={!controller.canScrollNext}
            aria-label="Next"
            circular
            size="$3.5"
            icon={<IconChevronRight size={16} />}
          />
        </XStack>
      }
    >
      <YStack ov="hidden">
        <ContentCarousel
          items={content}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          controllerRef={carouselRef}
          sizeConfig={sizeConfig}
        />
      </YStack>

      <XStack
        pt="$3.5"
        justifyContent="center"
        gap="$2"
        flexWrap="wrap"
        $gtSm={{ dsp: 'none' }}
      >
        <PaginationDots
          activeIndex={controller.currentIndex}
          count={content.length}
          onChange={(index) => controller.scrollToIndex(index)}
        />
      </XStack>
    </Section>
  )
}
